import { AllUnitEnum } from '../../models/convertUnitOfMeasureEnum'
import {
  InventoryPartEntry,
  InventoryPartInfoUnitOfMeasureEnum,
  InventoryPartInstanceEntry,
  InventoryPartInstanceInfoUnitOfMeasureEnum,
  ShopInventory
} from '../../sdk'
import { sendBugReport } from '../handleError/sendBugReport'
import { roundToTwoDecimal } from '../roundToTwoDecimal/roundToTwoDecimal'

export const MOTOR_UNIT_TO_ENUM: {
  [key: string]: string
} = {
  Ounce: InventoryPartInstanceInfoUnitOfMeasureEnum.Ounce as string,
  'Fluid Ounce': InventoryPartInstanceInfoUnitOfMeasureEnum.FlOz as string,
  milliliter: InventoryPartInstanceInfoUnitOfMeasureEnum.Ml as string,
  Liter: InventoryPartInstanceInfoUnitOfMeasureEnum.Liter as string,
  Quart: InventoryPartInstanceInfoUnitOfMeasureEnum.Quart as string
}

// One liter = 1.0566882 quarts
export const CONVERSION_TO_QUART: {
  [key: string]: number
} = {
  [InventoryPartInstanceInfoUnitOfMeasureEnum.Liter]: 1.0566882,
  [InventoryPartInstanceInfoUnitOfMeasureEnum.Ml]: 0.0010566882,
  [InventoryPartInstanceInfoUnitOfMeasureEnum.Gallon]: 4.0,
  [InventoryPartInstanceInfoUnitOfMeasureEnum.Quart]: 1.0,
  [InventoryPartInstanceInfoUnitOfMeasureEnum.Ounce]: 1.0 / 32,
  [InventoryPartInstanceInfoUnitOfMeasureEnum.FlOz]: 1.0 / 32
}
export function convertQuantityToDifferentUnit(
  quantity: number,
  originalUnit: AllUnitEnum,
  targetUnit: AllUnitEnum
) {
  return roundToTwoDecimal(
    convertQuantityToDifferentUnitRaw(quantity, originalUnit, targetUnit)
  )
}

export function convertQuantityToDifferentUnitRaw(
  quantity: number,
  originalUnit: AllUnitEnum,
  targetUnit: AllUnitEnum
) {
  if (originalUnit === targetUnit) {
    return quantity
  }
  if (
    String(originalUnit) ===
      (InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit as string) ||
    String(targetUnit) ===
      (InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit as string)
  ) {
    sendBugReport(
      'warning',
      'Cannot convert quantity between discrete unit and fluids unit.',
      ''
    )
    return 0
  }
  const quantityInQuart = CONVERSION_TO_QUART[String(originalUnit)] * quantity
  return quantityInQuart / CONVERSION_TO_QUART[String(targetUnit)]
}

export function convertUnitPriceToDifferentUnit(
  price: number,
  originalUnit: AllUnitEnum,
  targetUnit: AllUnitEnum
) {
  const quantity = 1
  const newQuantity = convertQuantityToDifferentUnit(
    1,
    originalUnit,
    targetUnit
  )
  return roundToTwoDecimal((price * quantity) / newQuantity)
}

export function getInventoryArrayQuantityInDesiredUnit(
  instances: InventoryPartInstanceEntry[],
  desiredUnit: AllUnitEnum
): number {
  return instances
    .map((i) =>
      convertQuantityToDifferentUnit(
        i.info!.quantity!,
        i.info!.unitOfMeasure!,
        desiredUnit
      )
    )
    .reduce((prev, cur) => prev + cur, 0)
}

export function sumAvailableUnitsMapInDesiredUnit(
  quantityMap: { [key: string]: number } | undefined,
  desiredUnit: AllUnitEnum
) {
  let quantity = 0
  if (!quantityMap) {
    return 0
  }
  if (
    String(desiredUnit) ===
    (InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit as string)
  ) {
    return (
      quantityMap[InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit] || 0
    )
  }
  for (const unit in quantityMap) {
    // if desired unit is fluids, ignore the discrete quantity
    if (unit === InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit) {
      continue
    }
    const unitQuantity = quantityMap[unit]
    quantity += convertQuantityToDifferentUnit(
      unitQuantity,
      unit as InventoryPartInstanceInfoUnitOfMeasureEnum,
      desiredUnit
    )
  }
  return roundToTwoDecimal(quantity)
}

export function getPartAvailableQuantity(
  partId: string,
  shopInventory: ShopInventory,
  desiredUnit?: AllUnitEnum | undefined
): number {
  const partEntry = shopInventory.allParts![partId]
  if (!partEntry) {
    return 0
  }
  if (desiredUnit) {
    return sumAvailableUnitsMapInDesiredUnit(
      shopInventory.availableUnits![partId],
      desiredUnit
    )
  }
  if (partEntry.info!.isFluids) {
    return sumAvailableUnitsMapInDesiredUnit(
      shopInventory.availableUnits![partId],
      InventoryPartInstanceInfoUnitOfMeasureEnum.Quart
    )
  }
  return sumAvailableUnitsMapInDesiredUnit(
    shopInventory.availableUnits![partId],
    desiredUnit || InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit
  )
}
export function getPartReceivedQuantity(
  partId: string,
  shopInventory: ShopInventory,
  desiredUnit?: AllUnitEnum | undefined
): number {
  const partEntry = shopInventory.allParts![partId]
  if (!partEntry) {
    return 0
  }
  if (desiredUnit) {
    return sumAvailableUnitsMapInDesiredUnit(
      shopInventory.receivedUnits![partId],
      desiredUnit
    )
  }
  if (partEntry.info!.isFluids) {
    return sumAvailableUnitsMapInDesiredUnit(
      shopInventory.receivedUnits![partId],
      InventoryPartInstanceInfoUnitOfMeasureEnum.Quart
    )
  }
  return sumAvailableUnitsMapInDesiredUnit(
    shopInventory.receivedUnits![partId],
    desiredUnit || InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit
  )
}
export function getPartPendingReturnQuantity(
  partId: string,
  shopInventory: ShopInventory,
  desiredUnit?: AllUnitEnum | undefined
): number {
  const partEntry = shopInventory.allParts![partId]
  if (!partEntry) {
    return 0
  }
  if (desiredUnit) {
    return sumAvailableUnitsMapInDesiredUnit(
      shopInventory.pendingReturnUnits![partId],
      desiredUnit
    )
  }
  if (partEntry.info!.isFluids) {
    return sumAvailableUnitsMapInDesiredUnit(
      shopInventory.pendingReturnUnits![partId],
      InventoryPartInstanceInfoUnitOfMeasureEnum.Quart
    )
  }
  return sumAvailableUnitsMapInDesiredUnit(
    shopInventory.pendingReturnUnits![partId],
    desiredUnit || InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit
  )
}
export function getPartPendingClaimedQuantity(
  partId: string,
  shopInventory: ShopInventory,
  desiredUnit?: AllUnitEnum | undefined
): number {
  const partEntry = shopInventory.allParts![partId]
  if (!partEntry) {
    return 0
  }
  if (desiredUnit) {
    return sumAvailableUnitsMapInDesiredUnit(
      shopInventory.pendingClaimedUnits![partId],
      desiredUnit
    )
  }
  if (partEntry.info!.isFluids) {
    return sumAvailableUnitsMapInDesiredUnit(
      shopInventory.pendingClaimedUnits![partId],
      InventoryPartInstanceInfoUnitOfMeasureEnum.Quart
    )
  }
  return sumAvailableUnitsMapInDesiredUnit(
    shopInventory.pendingClaimedUnits![partId],
    desiredUnit || InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit
  )
}
export function getPartInShopQuantity(
  partId: string,
  shopInventory: ShopInventory
): number {
  const partEntry = shopInventory.allParts![partId]
  if (!partEntry) {
    return 0
  }
  if (partEntry.info!.isFluids) {
    return sumAvailableUnitsMapInDesiredUnit(
      shopInventory.inShopUnits![partId],
      InventoryPartInstanceInfoUnitOfMeasureEnum.Quart
    )
  } else {
    return sumAvailableUnitsMapInDesiredUnit(
      shopInventory.inShopUnits![partId],
      InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit
    )
  }
}

export function getDefaultUnitForInventoryPart(
  inventoryPart: InventoryPartEntry
) {
  if (inventoryPart.info!.unitOfMeasure) {
    return inventoryPart.info!.unitOfMeasure
  }
  if (!inventoryPart.info!.isFluids) {
    return InventoryPartInfoUnitOfMeasureEnum.DiscreteUnit
  }
  return InventoryPartInfoUnitOfMeasureEnum.Quart
}
